import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

// Actions
import {
  getTargetGroupAttributes,
  getTargetGroups,
  removeSelectedTgId,
  setSelectedTgId,
  toggleCampaignCustomTgApplied,
} from "../../../actions/org/OrgTargetGroupActions";
import {
  getTgHeatMapData,
  removeTgHeatMapData,
} from "../../../actions/geo-data/GeoDataActions";

// Constants and Utils
import { DefaultPagination } from "../../../constants/GeneralConstants";
import {
  checkIsCustomTgPresent,
  getCustomTgPoiLayers,
} from "../../../utils/CampaignCustomTgUtils";

// Components
import Spinner from "../../../components/spinner/Spinner";
import SearchInputLocal from "../../../components/search-input-local/SearchInputLocal";

// Page Components
function TargetGroupInfo({
  eachTgInfo,
  bbox,
  selectedTgId = "",
  poiTypeLayerDataLoading = false,
}) {
  // Dispatch
  const dispatch = useDispatch();

  // to get id and all resultingPoiLayers
  const { id: tgId, resultLayers = [] } = eachTgInfo;

  const resPoiLayers = resultLayers.map((eachLayer) => eachLayer.poiTypeId);

  // selected and select button config
  const isTgSelected = selectedTgId == tgId;
  const buttonLabel = isTgSelected ? "Selected" : "Select";

  const buttonClassName = isTgSelected
    ? "btn-outline-success"
    : "btn-outline-primary";

  const buttonIconClassName = isTgSelected ? "fa fa-check" : "";

  // tgSelect loading
  const selectTgLoader = poiTypeLayerDataLoading && isTgSelected;

  // Function
  function getTgHeatMapFn() {
    dispatch(removeTgHeatMapData());
    dispatch(removeSelectedTgId());
    dispatch(setSelectedTgId(tgId));
    dispatch(getTgHeatMapData(resPoiLayers, bbox));

    // remove custom tg selection
    dispatch(toggleCampaignCustomTgApplied(false));
  }

  return (
    <div className="p-2 border-bottom navbar">
      <span className="px-2">{eachTgInfo.name}</span>
      <div>
        {selectTgLoader && <Spinner className="spinner-border-sm" />}
        <button
          disabled={selectTgLoader || isTgSelected}
          className={`btn ml-2 ${buttonClassName} add-button shadow-none`}
          onClick={getTgHeatMapFn}
        >
          <i className={buttonIconClassName}></i> {buttonLabel}{" "}
        </button>
      </div>
    </div>
  );
}

function CampaignCustomTgSelect({
  bbox,
  poiTypeLayerDataLoading = false,
  isCampaignCustomTgApplied = false,
}) {
  const dispatch = useDispatch();

  // campaign summary
  const { campaign = {} } = useSelector(
    (state) => state.campaignReport.campaignSummary
  );
  const { poiTypeIds = [], tgAttributesIds = [] } = campaign;

  // checks to call read tgAttr api
  const isCustomTgSelected = checkIsCustomTgPresent(campaign);

  // Selector
  const {
    targetGroupAttrs = {},
    // poiTypesMap = {}
  } = useSelector((state) => state.orgTargetGroup.originalData);

  // Only call the API when :
  // 1. tgAtt and poiTypes are selected for campaign
  // 2. api data not present in state
  useEffect(() => {
    if (isCustomTgSelected) {
      const secAttrNeeded = true;
      if (Object.keys(targetGroupAttrs).length === 0) {
        dispatch(getTargetGroupAttributes(secAttrNeeded));
      }
    }
  }, [dispatch, isCustomTgSelected, Object.keys(targetGroupAttrs).length]);

  // get resPoiLayers
  const customTgPoiLayers = getCustomTgPoiLayers(
    targetGroupAttrs,
    tgAttributesIds,
    poiTypeIds
  );

  const buttonClassName = isCampaignCustomTgApplied
    ? "btn-outline-success"
    : "btn-outline-primary";

  // selected and select button config
  const buttonLabel = isCampaignCustomTgApplied ? "Selected" : "Select";
  const buttonIconClassName = isCampaignCustomTgApplied ? "fa fa-check" : "";

  // tgSelect loading
  const selectTgLoader = poiTypeLayerDataLoading && isCampaignCustomTgApplied;

  // Function
  function getTgHeatMapFn() {
    dispatch(removeTgHeatMapData());
    dispatch(removeSelectedTgId());

    // set custom tg applied
    dispatch(toggleCampaignCustomTgApplied(true));

    // heatmap api call
    dispatch(getTgHeatMapData(customTgPoiLayers, bbox));
  }

  return (
    <div className="p-2 border-bottom navbar">
      <span className="px-2 font-weight-bold">Campaign Target Group</span>
      {!isCustomTgSelected && (
        <span className="font-italic text-muted mr-2">Not Selected</span>
      )}
      {isCustomTgSelected && (
        <div>
          {selectTgLoader && (
            <i
              className="fa fa-info-circle text-primary mr-2"
              title={
                "Please wait, Generating heatmap for this will take 1-2 minutes sometimes."
              }
            />
          )}
          {selectTgLoader && <Spinner className="spinner-border-sm" />}
          <button
            disabled={selectTgLoader || isCampaignCustomTgApplied}
            className={`btn ml-2 ${buttonClassName} add-button shadow-none`}
            onClick={getTgHeatMapFn}
          >
            <i className={buttonIconClassName}></i> {buttonLabel}{" "}
          </button>
        </div>
      )}
    </div>
  );
}

export default function TargetGroupListingSection({
  listingViewLabels,
  bbox,
  selectedTgId = "",

  // for campaign custom tg
  showCampaignCustomTg = false,
  isCampaignCustomTgApplied = false,
}) {
  const dispatch = useDispatch();

  // State
  const [targetGroups, setTargetGroups] = useState([]);

  // Selector State
  const getTgLoading = useSelector(
    (state) => state.orgTargetGroup.targetGroupLoading
  );

  // for listing of TG's (includes all information)
  const targetGroupsList = useSelector((state) => state.orgTargetGroup.tgList);

  // heatmap loading
  const poiTypeLayerDataLoading = useSelector(
    (state) => state.geoData.poiTypeLayerDataLoading
  );

  const { search } = listingViewLabels || {};

  // Pagination
  const { pageNumber, pageSize } = DefaultPagination;

  useEffect(() => {
    if (targetGroupsList.length === 0) {
      dispatch(getTargetGroups(true, "", pageNumber, pageSize));
    }
  }, [dispatch]);

  // Loading
  if (getTgLoading) {
    return (
      <div className="d-flex justify-content-center">
        <Spinner />
      </div>
    );
  }

  return (
    <>
      {/* Search Input Local */}
      <SearchInputLocal
        placeholder={search.placeholder}
        dataList={targetGroupsList}
        searchFields={search.fields}
        onSearchComplete={setTargetGroups}
      />
      <hr className="my-2" />

      {/* campaign custom Tg */}
      {showCampaignCustomTg && (
        <CampaignCustomTgSelect
          bbox={bbox}
          poiTypeLayerDataLoading={poiTypeLayerDataLoading}
          isCampaignCustomTgApplied={isCampaignCustomTgApplied}
        />
      )}

      {/* TG list table */}
      {targetGroups.map((eachTgInfo) => (
        <TargetGroupInfo
          key={eachTgInfo.id}
          eachTgInfo={eachTgInfo}
          bbox={bbox}
          selectedTgId={selectedTgId}
          poiTypeLayerDataLoading={poiTypeLayerDataLoading}
        />
      ))}

      {/* If there is no target group found */}
      {targetGroupsList.length < 1 && (
        <p className="text-center mt-4 font-italic">
          {"Target Groups are not available"}
        </p>
      )}
    </>
  );
}
